import './PageNameCellRenderer.scss';

import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { useTOCContext } from '@context/TOCContext';
import { Box } from '@mui/material';
import { changeEntryField, getModifiedFields } from '@utils/deckManagementEntityHelpers';
import { isNil } from 'lodash';
import { MouseEvent, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CellValueChangeParams, EditableCellParams, PageNameCellRendererProps } from 'types/TOC.types';
import { EnhancedTOCEntry } from 'types/TOCContext.types';

import EditableCellRenderer from './EditableCellRenderer';

const PageNameCellRenderer = (params: PageNameCellRendererProps) => {
  const navigate = useNavigate();
  const { isEditing, onEndEdit, onCellValueChange, deckId } = params;
  const { isGetDeckColumnForDeckLoading, pageIdToTemplateMap } = useTOCContext();
  const { deckLockStatus } = useDeckContext();
  const { toastNotification } = useAurigaContext();
  const editableParams: EditableCellParams = useMemo(
    () => ({
      data: params.data!,
      value: params.value,
      field: params.column?.getColId() || 'page_name',
      api: params.api,
    }),
    [params.api, params.column, params.data, params.value],
  );

  /** Checks if it's safe to navigate to the page.
   *
   * Page navigation is allowed only when:
   * 1. Deck Column data has loaded
   * 2. `params.data` is defined
   * 3. `params.data.page_name` is defined
   * 4. `page_name` has NOT been modified (i.e., it's not in the modified fields list)
   * 5. `params.data.page_id` is defined
   *
   * @returns boolean indicating if navigation is allowed
   */
  const canNavigateToPage = useCallback(
    (data?: EnhancedTOCEntry): data is EnhancedTOCEntry & { page_id: string; page_name: string } => {
      return (
        !isGetDeckColumnForDeckLoading &&
        !isNil(data) &&
        // Page Name Validations
        !isNil(data.page_name) &&
        !getModifiedFields(data).includes('page_name') &&
        // Page ID Validations
        !isNil(data.page_id)
      );
    },
    [isGetDeckColumnForDeckLoading],
  );

  /**
   * Handles cell value updates and shows a toast notification if the page name is modified.
   *
   * @param onChangeParams - Parameters containing information about the cell value change.
   */
  const handleUpdateCellValue = useCallback(
    (onChangeParams: CellValueChangeParams) => {
      onCellValueChange(onChangeParams);

      // Simulates a modification to determine if the field has been modified (or renamed back to original value).
      const testModify = changeEntryField(onChangeParams.data, onChangeParams.field, onChangeParams.newValue);
      if (getModifiedFields(testModify).includes('page_name')) {
        // Display toast notification if the page_name value has been modified.
        toastNotification({
          message: 'Page Name updated. To access the renamed page, Save Changes is required',
          visible: true,
          type: 'success',
        });
      }
    },
    [onCellValueChange, toastNotification],
  );

  /**
   * Handles click events on the page name cell.
   *
   * If the cell is not in editing mode and the data is valid, it either navigates to the page or opens a dialog.
   *
   * @param e - The mouse event triggered by clicking the cell.
   */
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!isEditing(editableParams)) {
        e.preventDefault();

        // Don't open dialog for new rows
        if (params.data?.metadata?.isNewRow || !canNavigateToPage(params.data)) {
          return;
        }

        if (pageIdToTemplateMap.has(params.data.page_id)) {
          // If pageId exists in map, redirect to the page using 'page_name' attribute (for PageName based routing)
          navigate(`/deck-preparation/decks/${deckId}/pages/${encodeURIComponent(params.data.page_name)}`);
        } else if (!deckLockStatus.isEditingDisabled) {
          // If pageId is not in map and deck is locked by current user, redirect to Page Setup Wizard
          navigate(`/deck-preparation/decks/${deckId}/page-setup/${encodeURIComponent(params.data.page_name)}`);
        }
      }
    },
    [
      canNavigateToPage,
      deckId,
      editableParams,
      isEditing,
      deckLockStatus.isEditingDisabled,
      navigate,
      pageIdToTemplateMap,
      params.data,
    ],
  );

  /**
   * Renders the appropriate content based on whether the cell is in edit mode or not.
   */
  const renderClickableContent = useCallback(() => {
    return (
      <Box
        component='span'
        className={`page-name-link ${isGetDeckColumnForDeckLoading ? 'disabled' : ''}`}
        onClick={isGetDeckColumnForDeckLoading ? undefined : handleClick}
        role='button'
        tabIndex={isGetDeckColumnForDeckLoading ? -1 : 0}
        onKeyDown={
          isGetDeckColumnForDeckLoading
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick({} as MouseEvent);
                }
              }
        }
      >
        {params.value}
      </Box>
    );
  }, [handleClick, isGetDeckColumnForDeckLoading, params.value]);

  /**
   * Renders static content (plain text) for the cell.
   * When the page needs setup but the deck is not locked, displays a span tooltip text on hover
   * informing the user they must lock the deck to configure the page.
   *
   * @param needsPageSetup - Whether the page requires initial setup (no template configured)
   */
  const renderStaticContent = useCallback(
    (needsPageSetup: boolean) => {
      return (
        <Box
          component='span'
          className={needsPageSetup ? 'page-name-needs-setup' : ''}
          title={needsPageSetup ? 'Lock Deck to set up this page' : undefined}
        >
          {params.value}
        </Box>
      );
    },
    [params.value],
  );

  if (isEditing(editableParams)) {
    return (
      <EditableCellRenderer
        {...params}
        column={params.column!}
        isEditing={isEditing}
        onEndEdit={onEndEdit}
        onCellValueChange={handleUpdateCellValue}
      />
    );
  }

  // If the component is in "loading" state, or if the page name is valid and can be navigated to, render clickable content; otherwise, render static content.
  // Pages needing setup are only clickable when the deck is locked by the current user (i.e. editing enabled).
  const needsPageSetup = canNavigateToPage(params.data) && !pageIdToTemplateMap.has(params.data.page_id);
  return isGetDeckColumnForDeckLoading ||
    (canNavigateToPage(params.data) && (!needsPageSetup || !deckLockStatus.isEditingDisabled))
    ? renderClickableContent()
    : renderStaticContent(needsPageSetup);
};

export default PageNameCellRenderer;
